import React from 'react';
import {
  Box,
  Button,
  Combobox,
  Divider,
  Flex,
  Text,
  Icon,
  Link,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
  useSnackbar,
  Tooltip,
  TextInput,
} from 'pounceV2';
import useModalV3 from 'Hooks/useModalV3';
import useInfiniteScroll from 'Hooks/useInfiniteScroll';
import { useTheme } from '@mui/material';
import { TablePlaceholderV3 } from 'Components/TablePlaceholder';
import FlatBadgeV3 from 'Components/badges/FlatBadge/FlatBadgeV3';
import UserAvatarV3 from 'Components/UserAvatar/UserAvatarV3';
import { Link as RouterLink } from 'react-router-dom';
import { extractErrorMessage, getActorDisplayName } from 'Helpers/utils';
import OptionsButtonV3 from 'Components/buttons/OptionsButton/OptionsButtonV3';
import useFeatureToggle from 'Hooks/useFeatureToggle';
import useUrlParams from 'Hooks/useUrlParams';
import urls from 'Source/urls';
import dayjs from 'dayjs';
import KendraSearchResults from 'Components/AIExplainLogEvents/KendraSearchResults';
import {
  useKendraSearch,
  getSearchLabel,
  SearchResultNode,
} from 'Components/AIExplainLogEvents/hooks/useKendraSearch';
import { useUpdateAIConversation } from '../graphql/updateAIConversation.generated';
import type { ConversationThread } from '../hooks/useConversationThreadsV3';
import { ConversationalAIParams, CONVERSATION_SCOPE } from '../types';

const SKELETON_PULSE_KEYFRAMES = {
  '0%, 100%': { opacity: 0.55 },
  '50%': { opacity: 1 },
} as const;

const ConversationRowSkeleton: React.FC<{ rowCount?: number }> = ({ rowCount = 5 }) => {
  const {
    palette: { tokens },
  } = useTheme();

  const barSx = React.useMemo(
    () => ({
      backgroundColor: tokens.background.neutral.raised[7],
      borderRadius: '4px',
      animation: 'conversationSkeletonPulse 1.4s ease-in-out infinite',
      '@keyframes conversationSkeletonPulse': SKELETON_PULSE_KEYFRAMES,
    }),
    [tokens.background.neutral.raised],
  );
  const circleBarSx = React.useMemo(() => ({ ...barSx, borderRadius: '50%' }), [barSx]);

  return (
    <Box width="100%">
      {Array.from({ length: rowCount }).map((_, index) => (
        <React.Fragment key={index}>
          {index > 0 && <Divider borderColor={tokens.stroke.neutral[4]} />}
          <Flex alignItems="center" gap={4} pl={5} pr={4} py={2.5} aria-hidden="true">
            <Flex direction="column" flex={1} minWidth={0} gap={1.5}>
              <Box height="14px" width="60%" sx={barSx} />
              <Box height="12px" width="75%" sx={barSx} />
            </Flex>
            <Flex alignItems="center" gap={2} flexShrink={0} width={160}>
              <Box height="20px" width="20px" sx={circleBarSx} />
              <Box height="12px" width="80px" sx={barSx} />
            </Flex>
            <Flex alignItems="center" gap={2} flex={1} minWidth={0}>
              <Box height="12px" width="60px" sx={barSx} />
            </Flex>
            <Box flexShrink={0} width={110} />
            <Box flexShrink={0}>
              <Box height="24px" width="24px" sx={barSx} />
            </Box>
          </Flex>
        </React.Fragment>
      ))}
    </Box>
  );
};

/**
 * Helper to get the display name for a conversation.
 */
const getConversationDisplayName = (conversation: ConversationThread): string => {
  if (conversation.title) {
    return conversation.title;
  }
  const id = conversation.id.slice(0, 8);
  const date = dayjs(conversation.createdAt).format('ddd, MMM D, h:mm:ss A');
  return `Chat ${id} - ${date}`;
};

/**
 * Conversation preview text — the immutable first user prompt captured at creation,
 * replacing the previous per-row streams(pageSize:1) sub-fetch.
 */
const getConversationPreview = (conversation: ConversationThread): string | null =>
  conversation.firstUserPrompt || null;

const ConversationRow = React.memo(
  ({
    conversation,
    onClose,
    onDelete,
    onConversationDeleted,
    onSelectConversation,
    enablePrivacyControls,
  }: {
    conversation: ConversationThread;
    onClose: () => void;
    onDelete: (id: string) => void;
    onConversationDeleted?: (id: string) => void;
    onSelectConversation?: (id: string) => void;
    enablePrivacyControls: boolean;
  }) => {
    const {
      palette: { tokens },
    } = useTheme();
    const {
      updateUrlParams,
      urlParams: { conversationId: activeConversationId },
    } = useUrlParams<ConversationalAIParams>();
    const { pushSnackbar } = useSnackbar();
    const displayName = React.useMemo(
      () => (conversation.createdBy ? getActorDisplayName(conversation.createdBy) : ''),
      [conversation.createdBy],
    );
    const [updateConversation] = useUpdateAIConversation();
    const { showModal, hideModal } = useModalV3();

    const conversationId = conversation.id;

    const handleConfirmDelete = React.useCallback(() => {
      hideModal();
      updateConversation({
        variables: { input: { conversationId, deleted: true } },
        refetchQueries: ['AIConversations'],
        awaitRefetchQueries: true,
        onCompleted: () => {
          onConversationDeleted?.(conversationId);
          onDelete(conversationId);
          pushSnackbar({
            variant: 'success',
            title: 'Conversation deleted',
          });
        },
        onError: error => {
          pushSnackbar({
            variant: 'error',
            title: 'Failed to delete conversation',
            description: extractErrorMessage(error),
          });
        },
      });
    }, [
      hideModal,
      onDelete,
      onConversationDeleted,
      conversationId,
      updateConversation,
      pushSnackbar,
    ]);

    const handleDeleteClick = React.useCallback(() => {
      showModal(
        <Box maxWidth={400}>
          <Text mb={6}>
            Are you sure you want to permanently delete this conversation? This action cannot be
            undone.
          </Text>
          <Flex justify="flex-end" gap={2}>
            <Button variant="outline" onClick={hideModal}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </Flex>
        </Box>,
        { title: 'Confirm Delete', showCloseButton: true },
      );
    }, [showModal, hideModal, handleConfirmDelete]);

    const handleRowClick = React.useCallback(() => {
      if (onSelectConversation) {
        onSelectConversation(conversation.id);
      } else {
        updateUrlParams({ conversationId: conversation.id });
      }
      onClose();
    }, [onSelectConversation, conversation.id, updateUrlParams, onClose]);

    const isSelected = conversation.id === activeConversationId;
    const displayTitle = getConversationDisplayName(conversation);
    const previewPrompt = getConversationPreview(conversation);

    return (
      <Box
        width="100%"
        cursor="pointer"
        role="button"
        tabIndex={0}
        transition="background-color 0.2s ease-in-out"
        onClick={handleRowClick}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRowClick();
          }
        }}
        sx={{
          backgroundColor: isSelected ? tokens.background.neutral.raised[7] : 'transparent',
          borderLeft: `4px solid ${isSelected ? tokens.stroke.accent[1].base : 'transparent'}`,
          '&:hover': {
            backgroundColor: tokens.background.neutral.raised[7],
          },
        }}
      >
        <Flex alignItems="center" gap={4} pl={5} pr={4} py={2.5}>
          {/* Title + preview */}
          <Flex direction="column" flex={1} minWidth={0}>
            <Text
              variant="body/md"
              fontWeight="medium"
              truncated
              color={tokens.content.text.neutral[1]}
            >
              {displayTitle}
            </Text>
            {previewPrompt && (
              <Text variant="body/md" truncated color={tokens.content.text.neutral[3]}>
                {previewPrompt}
              </Text>
            )}
          </Flex>

          {/* Avatar + Name */}
          {conversation.createdBy && (
            <Flex
              alignItems="center"
              justifyContent="flex-start"
              gap={2}
              flexShrink={0}
              width={160}
            >
              <UserAvatarV3 name={displayName} entityId={conversation.createdBy.id} />
              <Text variant="body/md" truncated color={tokens.content.text.neutral[1]}>
                {displayName}
              </Text>
            </Flex>
          )}

          {/* CreatedAt + Badges */}
          <Flex alignItems="center" justifyContent="flex-start" gap={2} flex={1} minWidth={0}>
            <Tooltip
              content={dayjs(conversation.createdAt).format('MMMM D, YYYY h:mm A')}
              placement="left"
            >
              <Text variant="body/md" whiteSpace="nowrap" color={tokens.content.text.neutral[1]}>
                {dayjs(conversation.createdAt).fromNow()}
              </Text>
            </Tooltip>

            {enablePrivacyControls && !conversation.private && <FlatBadgeV3>Shared</FlatBadgeV3>}

            {conversation.isScheduled && (
              <FlatBadgeV3>
                <Flex alignItems="center" gap={1}>
                  <Icon type="clock" size="x-small" /> Scheduled
                </Flex>
              </FlatBadgeV3>
            )}
          </Flex>

          {/* View Schedule Link */}
          <Flex flexShrink={0} width={110} justifyContent="flex-start" alignItems="center">
            {conversation.isScheduled && (
              <Link
                as={RouterLink}
                to={
                  conversation.scheduledPromptId
                    ? urls.ai.scheduledPrompts.details(conversation.scheduledPromptId)
                    : urls.ai.scheduledPrompts.list()
                }
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                fontSize="0.75rem"
              >
                View schedule
              </Link>
            )}
          </Flex>

          {/* Options — always visible inline */}
          {(!enablePrivacyControls || conversation.canEdit) && (
            <Box flexShrink={0} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <Dropdown>
                <DropdownButton as={OptionsButtonV3} aria-label="Toggle Conversation Options" />
                <DropdownMenu>
                  <Tooltip content="Permanently delete this conversation" placement="left">
                    <DropdownItem onSelect={handleDeleteClick}>Delete conversation</DropdownItem>
                  </Tooltip>
                </DropdownMenu>
              </Dropdown>
            </Box>
          )}
        </Flex>
      </Box>
    );
  },
);

ConversationRow.displayName = 'ConversationRow';

const ConversationGroup = ({
  title,
  conversations,
  onClose,
  onDelete,
  onConversationDeleted,
  onSelectConversation,
  enablePrivacyControls,
}: {
  title?: string;
  conversations: ConversationThread[];
  onClose: () => void;
  onDelete: (id: string) => void;
  onConversationDeleted?: (id: string) => void;
  onSelectConversation?: (id: string) => void;
  enablePrivacyControls: boolean;
}) => {
  const {
    palette: { tokens },
  } = useTheme();

  if (!conversations.length) {
    return null;
  }

  return (
    <Box>
      {title && (
        <Text
          variant="body/sm"
          mb={2}
          px={6}
          color={tokens.content.text.accent[14][2]}
          fontWeight="bold"
        >
          {title}
        </Text>
      )}
      {conversations.map((conversation, index) => (
        <React.Fragment key={conversation.id}>
          {index > 0 && <Divider borderColor={tokens.stroke.neutral[4]} />}
          <ConversationRow
            conversation={conversation}
            onClose={onClose}
            onDelete={onDelete}
            onConversationDeleted={onConversationDeleted}
            onSelectConversation={onSelectConversation}
            enablePrivacyControls={enablePrivacyControls}
          />
        </React.Fragment>
      ))}
    </Box>
  );
};

interface ConversationThreadListV3Props {
  onClose: () => void;
  conversations: ConversationThread[];
  loading: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  topOffset?: number;
  onSelectConversation?: (id: string) => void;
  onConversationDeleted?: (id: string) => void;
  alias?: string;
}

const ConversationThreadListV3: React.FC<ConversationThreadListV3Props> = ({
  onClose,
  conversations,
  loading,
  hasNextPage,
  onLoadMore,
  topOffset = 0,
  onSelectConversation,
  onConversationDeleted,
  alias,
}) => {
  const handleSentinelIntersect = React.useCallback(() => {
    if (hasNextPage) {
      onLoadMore?.();
    }
  }, [hasNextPage, onLoadMore]);
  const { sentinelRef } = useInfiniteScroll<HTMLDivElement>({
    loading,
    threshold: 500,
    onLoadMore: handleSentinelIntersect,
  });
  const {
    palette: { tokens },
  } = useTheme();
  const enablePrivacyControls = useFeatureToggle('ENABLE_AI_PRIVACY_CONTROLS');
  const { updateUrlParams, urlParams } = useUrlParams<ConversationalAIParams>();

  const { scope } = urlParams;

  const {
    searchQuery,
    setSearchQuery,
    searchAll,
    searchMine,
    showSearchResults,
    hasSearched,
    allSearchResults,
    handleSearch,
    handleClearResults,
    handleLoadMore,
    searchLoading,
    searchError,
    isKendraEnabled,
    totalCount,
    searchHasNextPage,
  } = useKendraSearch({ isConversation: true, alias, scope });

  const handleDelete = React.useCallback(
    (id: string) => {
      if (id === urlParams.conversationId) {
        updateUrlParams({ conversationId: undefined });
      }
    },
    [updateUrlParams, urlParams.conversationId],
  );

  const handleSearchResultClick = React.useCallback(
    (node: SearchResultNode) => {
      if (node.conversationId) {
        updateUrlParams({ conversationId: node.conversationId });
        onClose();
      }
    },
    [updateUrlParams, onClose],
  );

  return (
    <Flex
      direction="column"
      overflow="auto"
      gap={5}
      zIndex={1}
      position="absolute"
      top={topOffset}
      left={0}
      right={0}
      bottom={0}
      py={6}
      sx={{
        backgroundColor: tokens.background.neutral.canvas.base,
        transition: 'transform 0.3s ease-in-out',
      }}
    >
      <Box px={6} pb={4} mx="auto" width="100%">
        {/* Scope dropdown + search input — always rendered, Kendra search gated below */}
        <Flex alignItems="center" gap={2}>
          <Box width={100} flexShrink={0}>
            <Combobox
              label="Filter"
              items={[CONVERSATION_SCOPE.ALL, CONVERSATION_SCOPE.OWNED]}
              value={scope ?? CONVERSATION_SCOPE.ALL}
              itemToString={item => (item === CONVERSATION_SCOPE.OWNED ? 'Mine' : 'All')}
              showClearSelectionControl={false}
              onChange={newScope =>
                updateUrlParams({
                  scope:
                    newScope === CONVERSATION_SCOPE.OWNED ? CONVERSATION_SCOPE.OWNED : undefined,
                })
              }
            />
          </Box>

          {/* Kendra search input */}
          {isKendraEnabled && (
            <Box flex={1}>
              <TextInput
                label={getSearchLabel(searchAll, searchMine, alias, true)}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleSearch(searchQuery);
                  }
                }}
                placeholder="Search conversations..."
                icon="search"
                iconAlignment="left"
                style={{ width: '100%' }}
              />
            </Box>
          )}
        </Flex>

        {isKendraEnabled && (
          <>
            {searchLoading && allSearchResults.length === 0 && (
              <Box py={2}>
                <TablePlaceholderV3 rowCount={2} />
              </Box>
            )}
            {searchError && (
              <Box py={2}>
                <Text color={tokens.content.text.semantic.error.base} variant="body/sm">
                  Search failed: {searchError.message || 'Unknown error'}
                </Text>
              </Box>
            )}
            {showSearchResults && allSearchResults.length > 0 && (
              <Box pt="5px">
                <KendraSearchResults
                  results={allSearchResults}
                  totalCount={totalCount ?? undefined}
                  hasNextPage={searchHasNextPage}
                  loading={searchLoading}
                  onResultClick={handleSearchResultClick}
                  onClearResults={handleClearResults}
                  onLoadMore={handleLoadMore}
                />
              </Box>
            )}
            {showSearchResults &&
              hasSearched &&
              allSearchResults.length === 0 &&
              !searchLoading && (
                <Box py={4} textAlign="center">
                  <Text color={tokens.content.text.accent[14][2]}>
                    No conversations found that match your search.
                  </Text>
                </Box>
              )}
          </>
        )}
      </Box>

      <Box width="100%" px={6}>
        <ConversationGroup
          conversations={conversations}
          onClose={onClose}
          onDelete={handleDelete}
          onConversationDeleted={onConversationDeleted}
          onSelectConversation={onSelectConversation}
          enablePrivacyControls={enablePrivacyControls}
        />
        {loading && (
          <Box
            data-testid="conversations-loading"
            role="status"
            aria-live="polite"
            aria-label="Loading conversations"
          >
            <ConversationRowSkeleton rowCount={conversations.length === 0 ? 5 : 2} />
          </Box>
        )}
        {!loading && !conversations.length && (
          <Box py={8} textAlign="center">
            <Text color={tokens.content.text.neutral[3]}>
              {scope === CONVERSATION_SCOPE.OWNED
                ? 'No conversations owned by you.'
                : 'No conversations yet. Start a new chat to get going.'}
            </Text>
          </Box>
        )}
        {/* Suppress the sentinel when the rendered list is empty so a filter that
            hides every row can't auto-paginate the server until exhaustion. */}
        {hasNextPage && conversations.length > 0 && <Box ref={sentinelRef} height="1px" />}
      </Box>
    </Flex>
  );
};

export default ConversationThreadListV3;
